import { useCallback, useEffect, useState } from "react";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { Avatar, Loader } from "@mantine/core";
import { translateUserTypeToTurkish } from "../../utils/frontFix";
import { meAtom } from "../../store/jotaiStore";
import { clearTokenData, getRefreshToken } from "../../services/tokenManager";
import authService from "../../services/authService";
import { Appointment } from "../../interfaces/services/Apoointment";
import appointmentService from "../../services/appointmentService";
import CustomerAppointments from "../../components/CustomerAppointments";
import UpdateProfileModal from "../../components/UpdateProfileModal";
import ChangePasswordModal from "../../components/ChangePasswordModal";

const Profile = () => {
  const [me, setMe] = useAtom(meAtom);
  const navigation = useNavigate();

  const logOut = async () => {
    await authService.logOut(getRefreshToken()!);
    clearTokenData();
    setMe(undefined);
    navigation("/");
  };

  const [showPwdModal, setShowPwdModal] = useState(false);
  const [showUpdtModal, setShowUpdtModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const getAppointments = useCallback(async () => {
    if (!me) return;
    setLoading(true);
    try {
      const res = await appointmentService.getMyAppointments();
      setAppointments(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [me?.salonCode]);

  useEffect(() => {
    if (!me) {
      navigation("/giris-yap");
      return;
    }
    getAppointments();
  }, [getAppointments]);

  if (!me)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader color="blue" size="xl" />
      </div>
    );

  return (
    <div className="bg-background-light/50 dark:bg-background-dark/50">
      <div className="container mx-auto px-4 py-8 bg-background-light dark:bg-background-dark">
        {/* Profil Kartı */}
        <div className="relative z-10 bg-gradient-to-r from-white via-blue-50 to-white dark:from-background-dark dark:via-blue-900/30 dark:to-background-dark rounded-xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="relative">
                <Avatar
                  src={me.imageUrl}
                  alt="Profil"
                  size="xl"
                  name={me.name + " " + me.lastname}
                />
                <div className="absolute -bottom-1 -right-1 bg-green-500 w-6 h-6 rounded-full border-2 border-white dark:border-gray-800"></div>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-text-light dark:text-text-dark">
                  {me.name} {me.lastname}
                </h1>
                <p className="text-sm md:text-base text-ttext-light dark:text-ttext-dark flex items-center gap-2">
                  <span className="bg-blue-100 dark:bg-blue-900/50 px-2 py-0.5 rounded-full text-blue-700 dark:text-blue-300 text-xs font-medium">
                    {translateUserTypeToTurkish(me.type)}
                  </span>
                  <span>{me.phone}</span>
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row flex-wrap gap-3 md:items-center">
              <button
                aria-label="Profili Düzenle"
                onClick={() => setShowUpdtModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all shadow-md hover:shadow-lg"
              >
                Profili Düzenle
              </button>
              <button
                aria-label="Şifre Değiştir"
                onClick={() => setShowPwdModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all shadow-md hover:shadow-lg"
              >
                Şifre Değiştir
              </button>
              <button
                aria-label="Çıkış Yap"
                onClick={logOut}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all shadow-md hover:shadow-lg"
              >
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-2">
          <CustomerAppointments appointments={appointments} loading={loading} />
        </div>

        {/* Modaller */}
        <ChangePasswordModal
          visible={showPwdModal}
          onClose={() => setShowPwdModal(false)}
        />
        <UpdateProfileModal
          visible={showUpdtModal}
          onClose={() => setShowUpdtModal(false)}
        />
      </div>
    </div>
  );
};

export default Profile;
